import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import {
  CloseChatSession,
  CloseChatSessionFail,
  CloseChatSessionSuccess,
  CreateChatSession,
  CreateChatSessionFail,
  CreateChatSessionSuccess,
  SendMessage,
  SendMessageFail,
  SendMessageSuccess,
} from '../actions/ai-chat.actions';
import { catchError, EMPTY, exhaustMap, map, of, withLatestFrom } from 'rxjs';
import { AiChatServiceService } from '../../services/ai-chat-service.service';
import { Store } from '@ngrx/store';
import { selectToken } from '../selectors/ai-chat.selectors';

@Injectable()
export class AiChatEffects {
  constructor(
    private actions$: Actions,
    private aiChatService: AiChatServiceService,
    private store: Store
  ) {}

  createChatSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CreateChatSession),
      exhaustMap((_action) => {
        // Simulate API call to create chat session
        return this.aiChatService.createChatSession().pipe(
          map((token) => CreateChatSessionSuccess({ token })),
          catchError((error) => of(CreateChatSessionFail({ error })))
        );
      })
    );
  });

  sendMessage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SendMessage),
      concatLatestFrom(() => this.store.select(selectToken)),
      exhaustMap(([action, token]) => {
        // Simulate API call to send message
        return this.aiChatService.sendMessage(token, action.message.message).pipe(
          map((response) => SendMessageSuccess({ response })),
          catchError((error) => of(SendMessageFail({ error })))
        );
      })
    );
  });

  closeChatSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CloseChatSession),
      concatLatestFrom(() => this.store.select(selectToken)),
      exhaustMap(([action, token]) => {
        // Simulate API call to close chat session
        return this.aiChatService.closeChatSession(token).pipe(
          map(() => CloseChatSessionSuccess({ isReset: action.isReset })),
          catchError((error) => of(CloseChatSessionFail({ error })))
        );
      })
    );
  });

  closeChatSessionSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CloseChatSessionSuccess),
      map(({ isReset }) => {
        if (isReset) {
          return CreateChatSession();
        }
        // Return a no-op action
        return { type: '[AI Chat] No Action' };
      })
    );
  });
}
